/**
 * What each editable tenant field is called on screen, and how a before/after
 * value is rendered.
 *
 * TWO SCREENS READ FROM HERE and they must agree. The edit form lists the fields
 * a save is about to change; the audit panel lists the fields a save *did*
 * change. If those two lists name the same column differently — "Valid till" in
 * one, "Subscription expiry" in the other — the operator cannot tell that the
 * row in the history is the change they just made. So the labels are one map,
 * typed `Record<TenantEditFieldPath, string>`, which means a field added to
 * `TENANT_EDIT_FIELD_PATHS` in `@wend/shared` fails this app's typecheck until
 * it has been named here.
 *
 * A BOOLEAN IS NOT RENDERED AS "TRUE". `isActive` is the only boolean in the
 * set and it is the one field on this screen with a consequence a person needs
 * to read at a glance: `false` means nobody in that workspace can sign in. An
 * audit row saying `isActive: true → false` is technically complete and
 * practically unreadable; "Active → Suspended" is the same fact in the words the
 * rest of the admin panel already uses for it. Hence {@link formatAuditValue}
 * taking the field as well as the value — a generic value renderer cannot know
 * that, and would produce "Yes → No".
 *
 * `null` IS RENDERED AS A SENTENCE, NOT AS A BLANK. An audit row whose `after`
 * is null is a field that was deliberately *cleared*, and a blank cell there is
 * indistinguishable from a rendering bug. It reads "(cleared)" for that reason —
 * and "(empty)" on the before side, where the meaning is that there was nothing
 * there to begin with.
 */
import type { AdminAuditValue, TenantEditFieldPath } from '@wend/shared';

import { formatDateTime } from './format';

/** One entry per path in `TENANT_EDIT_FIELD_PATHS`. See the header. */
export const TENANT_FIELD_LABEL: Readonly<Record<TenantEditFieldPath, string>> = {
  name: 'Workspace name',
  email: 'Contact email',
  phone: 'Contact phone',
  'organisation.legalName': 'Legal name',
  'organisation.website': 'Website',
  'organisation.addressLine1': 'Address line 1',
  'organisation.addressLine2': 'Address line 2',
  'organisation.city': 'City',
  'organisation.state': 'State',
  'organisation.postalCode': 'Postal code',
  'organisation.country': 'Country',
  'organisation.taxId': 'Tax ID',
  validTill: 'Subscription expiry',
  isActive: 'Sign-in',
  features: 'Features',
};

/**
 * A before/after value as a person reads it.
 *
 * `side` decides only what a `null` says: nothing was there, versus it was
 * emptied. The distinction is the whole reason the parameter exists — both are
 * `null` on the wire and they are not the same event.
 */
export function formatAuditValue(
  field: TenantEditFieldPath,
  value: AdminAuditValue,
  side: 'before' | 'after',
): string {
  if (value === null) return side === 'before' ? '(empty)' : '(cleared)';

  if (field === 'isActive') {
    // Not "Yes"/"No": the two words the rest of the panel uses for this column.
    return value === true ? 'Active — can sign in' : 'Suspended — cannot sign in';
  }

  if (field === 'validTill' && typeof value === 'string') {
    // The audit row stores the instant, which is the right thing to store and
    // the wrong thing to show. Falls back to the raw value rather than to the
    // em dash `formatDateTime` uses for an unparseable date: a stored expiry
    // this app cannot parse is a fact worth seeing, not one worth tidying away.
    return Number.isNaN(Date.parse(value)) ? value : formatDateTime(value);
  }

  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  return String(value);
}
